/**
 * Unified Marketplace Status Constants
 * 
 * Canonical taxonomy for marketplace requests and responses across all 3 apps:
 * - apps/marketplace-standalone/ (API cache/passthrough)
 * - apps/contractor-app/src/components/Marketplace/ (Contractor view)
 * - apps/customer-portal/src/ (Customer view)
 * 
 * This file provides:
 * - Status constants for request and response lifecycles
 * - Display labels for each status
 * - Color schemes for each status (Tailwind CSS)
 * - Helper functions for status display
 * - Backward compatibility mapping for legacy DB values
 */

// ============================================
// REQUEST LIFECYCLE STATUSES
// ============================================

export const REQUEST_STATUS = {
  DRAFT: 'draft',
  POSTED: 'posted',
  BIDDING_OPEN: 'bidding_open',
  BIDDING_CLOSED: 'bidding_closed',
  AWARDED: 'awarded',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CLOSED: 'closed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  DISPUTED: 'disputed'
};

// ============================================
// RESPONSE LIFECYCLE STATUSES
// ============================================

export const RESPONSE_STATUS = {
  PENDING: 'pending',
  SUBMITTED: 'submitted',
  SHORTLISTED: 'shortlisted',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  WITHDRAWN: 'withdrawn',
  EXPIRED: 'expired'
};

// Legacy status mappings for backward compatibility
// DB values that should map to canonical statuses
export const LEGACY_REQUEST_STATUS_MAP = {
  // Legacy values found in DB -> Canonical status
  'available': REQUEST_STATUS.POSTED,
  'open': REQUEST_STATUS.BIDDING_OPEN,
  'available': REQUEST_STATUS.POSTED,
  'contractor_selected': REQUEST_STATUS.AWARDED,
  'work_in_progress': REQUEST_STATUS.IN_PROGRESS,
  'hired': REQUEST_STATUS.AWARDED,
  'bidding_open': REQUEST_STATUS.BIDDING_OPEN,
  'bidding_closed': REQUEST_STATUS.BIDDING_CLOSED
};

export const LEGACY_RESPONSE_STATUS_MAP = {
  // Legacy values found in DB -> Canonical status
  'interested': RESPONSE_STATUS.SUBMITTED,
  'interested_estimate': RESPONSE_STATUS.SUBMITTED,
  'offer': RESPONSE_STATUS.SUBMITTED,
  'question': RESPONSE_STATUS.SUBMITTED,
  'unavailable': RESPONSE_STATUS.REJECTED,
  'pending_quote': RESPONSE_STATUS.PENDING,
  'hired': RESPONSE_STATUS.ACCEPTED,
  'PENDING_QUOTE': RESPONSE_STATUS.PENDING,
  'ACCEPTED': RESPONSE_STATUS.ACCEPTED,
  'REJECTED': RESPONSE_STATUS.REJECTED,
  'approved': RESPONSE_STATUS.ACCEPTED,
  'declined': RESPONSE_STATUS.REJECTED
};

// ============================================
// STATUS DISPLAY LABELS
// ============================================

export const REQUEST_STATUS_LABELS = {
  [REQUEST_STATUS.DRAFT]: 'Draft',
  [REQUEST_STATUS.POSTED]: 'Posted',
  [REQUEST_STATUS.BIDDING_OPEN]: 'Open for Bids',
  [REQUEST_STATUS.BIDDING_CLOSED]: 'Bidding Closed',
  [REQUEST_STATUS.AWARDED]: 'Contractor Selected',
  [REQUEST_STATUS.IN_PROGRESS]: 'In Progress',
  [REQUEST_STATUS.COMPLETED]: 'Completed',
  [REQUEST_STATUS.CLOSED]: 'Closed',
  [REQUEST_STATUS.CANCELLED]: 'Cancelled',
  [REQUEST_STATUS.EXPIRED]: 'Expired',
  [REQUEST_STATUS.DISPUTED]: 'Disputed'
};

export const RESPONSE_STATUS_LABELS = {
  [RESPONSE_STATUS.PENDING]: 'Pending Review',
  [RESPONSE_STATUS.SUBMITTED]: 'Submitted',
  [RESPONSE_STATUS.SHORTLISTED]: 'Shortlisted',
  [RESPONSE_STATUS.ACCEPTED]: 'Accepted',
  [RESPONSE_STATUS.REJECTED]: 'Not Selected',
  [RESPONSE_STATUS.WITHDRAWN]: 'Withdrawn',
  [RESPONSE_STATUS.EXPIRED]: 'Expired'
};

// ============================================
// STATUS COLORS (Tailwind CSS classes)
// ============================================

export const REQUEST_STATUS_COLORS = {
  [REQUEST_STATUS.DRAFT]: 'text-gray-600 bg-gray-100',
  [REQUEST_STATUS.POSTED]: 'text-blue-600 bg-blue-100',
  [REQUEST_STATUS.BIDDING_OPEN]: 'text-green-600 bg-green-100',
  [REQUEST_STATUS.BIDDING_CLOSED]: 'text-yellow-600 bg-yellow-100',
  [REQUEST_STATUS.AWARDED]: 'text-purple-600 bg-purple-100',
  [REQUEST_STATUS.IN_PROGRESS]: 'text-indigo-600 bg-indigo-100',
  [REQUEST_STATUS.COMPLETED]: 'text-green-600 bg-green-100',
  [REQUEST_STATUS.CLOSED]: 'text-gray-600 bg-gray-100',
  [REQUEST_STATUS.CANCELLED]: 'text-red-600 bg-red-100',
  [REQUEST_STATUS.EXPIRED]: 'text-orange-600 bg-orange-100',
  [REQUEST_STATUS.DISPUTED]: 'text-red-600 bg-red-100'
};

export const RESPONSE_STATUS_COLORS = {
  [RESPONSE_STATUS.PENDING]: 'text-yellow-600 bg-yellow-100',
  [RESPONSE_STATUS.SUBMITTED]: 'text-blue-600 bg-blue-100',
  [RESPONSE_STATUS.SHORTLISTED]: 'text-purple-600 bg-purple-100',
  [RESPONSE_STATUS.ACCEPTED]: 'text-green-600 bg-green-100',
  [RESPONSE_STATUS.REJECTED]: 'text-red-600 bg-red-100',
  [RESPONSE_STATUS.WITHDRAWN]: 'text-gray-600 bg-gray-100',
  [RESPONSE_STATUS.EXPIRED]: 'text-orange-600 bg-orange-100'
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Normalize a request status value for display
 * Maps legacy DB values to canonical statuses
 * @param {string} status - The raw status value from DB or UI
 * @returns {string} The canonical status
 */
export const normalizeRequestStatus = (status) => {
  if (!status) return REQUEST_STATUS.DRAFT;
  const normalized = String(status).toLowerCase();
  return LEGACY_REQUEST_STATUS_MAP[normalized] || normalized;
};

/**
 * Normalize a response status value for display
 * Maps legacy DB values to canonical statuses
 * @param {string} status - The raw status value from DB or UI
 * @returns {string} The canonical status
 */
export const normalizeResponseStatus = (status) => {
  if (!status) return RESPONSE_STATUS.PENDING;
  const normalized = String(status).toUpperCase();
  return LEGACY_RESPONSE_STATUS_MAP[normalized] || normalized.toLowerCase();
};

/**
 * Get display label for a request status
 * @param {string} status - The status value
 * @returns {string} Human-readable label
 */
export const getRequestStatusLabel = (status) => {
  const normalized = normalizeRequestStatus(status);
  return REQUEST_STATUS_LABELS[normalized] || status || 'Unknown';
};

/**
 * Get display label for a response status
 * @param {string} status - The status value
 * @returns {string} Human-readable label
 */
export const getResponseStatusLabel = (status) => {
  const normalized = normalizeResponseStatus(status);
  return RESPONSE_STATUS_LABELS[normalized] || status || 'Unknown';
};

/**
 * Display label for a request TYPE.
 *
 * Lives here, next to the status labels, because it is the same job for the same table and
 * a second local copy is how the two drift apart. There was no type label anywhere, so
 * MyRequests title-cased the raw column with
 *     request_type.charAt(0).toUpperCase() + request_type.slice(1)
 * which is correct for none of the three live values: 'STANDARD' rendered as the shouty
 * "STANDARD", 'customer_service' rendered as "Customer_service" with the underscore showing,
 * and 'EMERGENCY' as "EMERGENCY". Measured 2026-09-01, that is 74 of 88 requests displaying
 * a raw enum to a customer.
 *
 * Case-insensitive because the column genuinely holds both cases — 'STANDARD' and
 * 'EMERGENCY' uppercase, 'customer_service' lower — and normalising the read is cheaper and
 * safer than a data migration nothing else depends on.
 *
 * @param {string} requestType - The request_type value (may be null)
 * @returns {string|null} Human-readable label, or null when there is no type to show
 */
export const REQUEST_TYPE_LABELS = {
  standard: 'Standard',
  emergency: 'Emergency',
  customer_service: 'Customer Service'
};

export const getRequestTypeLabel = (requestType) => {
  if (!requestType) return null;
  const key = String(requestType).trim().toLowerCase();
  if (REQUEST_TYPE_LABELS[key]) return REQUEST_TYPE_LABELS[key];
  // Unknown value: humanise rather than echo the enum — underscores to spaces, title case.
  return key
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Get color classes for a request status
 * @param {string} status - The status value
 * @returns {string} Tailwind CSS classes
 */
export const getRequestStatusColor = (status) => {
  const normalized = normalizeRequestStatus(status);
  return REQUEST_STATUS_COLORS[normalized] || 'text-gray-600 bg-gray-100';
};

/**
 * Get color classes for a response status
 * @param {string} status - The status value
 * @returns {string} Tailwind CSS classes
 */
export const getResponseStatusColor = (status) => {
  const normalized = normalizeResponseStatus(status);
  return RESPONSE_STATUS_COLORS[normalized] || 'text-gray-600 bg-gray-100';
};

/**
 * Generic status label getter - automatically detects request vs response
 * @param {string} status - The status value
 * @param {string} type - Optional: 'request' or 'response' for explicit typing
 * @returns {string} Human-readable label
 */
export const getStatusLabel = (status, type) => {
  // Try to auto-detect type based on common patterns
  if (!type) {
    const normalized = String(status).toLowerCase();
    if (['accepted', 'rejected', 'pending', 'submitted', 'shortlisted', 'withdrawn'].includes(normalized)) {
      return getResponseStatusLabel(status);
    }
    return getRequestStatusLabel(status);
  }
  
  return type === 'response' ? getResponseStatusLabel(status) : getRequestStatusLabel(status);
};

/**
 * Generic status color getter - automatically detects request vs response
 * @param {string} status - The status value
 * @param {string} type - Optional: 'request' or 'response' for explicit typing
 * @returns {string} Tailwind CSS classes
 */
export const getStatusColor = (status, type) => {
  // Try to auto-detect type based on common patterns
  if (!type) {
    const normalized = String(status).toLowerCase();
    if (['accepted', 'rejected', 'pending', 'submitted', 'shortlisted', 'withdrawn'].includes(normalized)) {
      return getResponseStatusColor(status);
    }
    return getRequestStatusColor(status);
  }
  
  return type === 'response' ? getResponseStatusColor(status) : getRequestStatusColor(status);
};

/**
 * Check if a request status represents an active/open state
 * @param {string} status - The status value
 * @returns {boolean}
 */
export const isRequestActive = (status) => {
  const normalized = normalizeRequestStatus(status);
  return [REQUEST_STATUS.POSTED, REQUEST_STATUS.BIDDING_OPEN].includes(normalized);
};

/**
 * Check if a response status represents a terminal state
 * @param {string} status - The status value
 * @returns {boolean}
 */
export const isResponseTerminal = (status) => {
  const normalized = normalizeResponseStatus(status);
  return [RESPONSE_STATUS.ACCEPTED, RESPONSE_STATUS.REJECTED, RESPONSE_STATUS.WITHDRAWN, RESPONSE_STATUS.EXPIRED].includes(normalized);
};

// ============================================
// EXPORTS
// ============================================

export default {
  // Status constants
  REQUEST_STATUS,
  RESPONSE_STATUS,
  
  // Legacy mappings
  LEGACY_REQUEST_STATUS_MAP,
  LEGACY_RESPONSE_STATUS_MAP,
  
  // Labels
  REQUEST_STATUS_LABELS,
  RESPONSE_STATUS_LABELS,
  REQUEST_TYPE_LABELS,
  
  // Colors
  REQUEST_STATUS_COLORS,
  RESPONSE_STATUS_COLORS,
  
  // Helper functions
  normalizeRequestStatus,
  normalizeResponseStatus,
  getRequestStatusLabel,
  getResponseStatusLabel,
  getRequestTypeLabel,
  getStatusLabel,
  getRequestStatusColor,
  getResponseStatusColor,
  getStatusColor,
  isRequestActive,
  isResponseTerminal
};
